import { InputAdornment, SxProps, TextField, Theme } from '@mui/material';
import { InputProps } from '@mui/material/Input/Input';
import {
  memo,
  useCallback,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type FocusEvent,
  type ReactElement,
} from 'react';
import { useTranslation } from 'react-i18next';

import { StyledTooltip } from '@/modules/common/components/StyledTooltip';
import { useChangeEvent } from '../hooks/useChangeEvent';
import { useEnterBlur } from '../hooks/useEnterBlur';
import { OnChangeHandler } from '../types';

const escapeSpecialRegexCharsRegex = /[.*+?^${}()|[\]\\]/gm;
const ALWAYS_FORBIDDEN_CHARACTERS = '%';

type Props = {
  ariaLabel?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  error?: boolean;
  errorType?: 'normal' | 'floating';
  helperText?: string;
  prefix?: ReactElement;
  suffix?: ReactElement;
  inputProps?: Partial<InputProps>;
  enterBlur?: boolean;
  forbiddenCharacters?: string;
  onBlur?: () => void;
  onChange?: OnChangeHandler<string>;
  onFocus?: (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  placeholder?: string;
  sx?: SxProps<Theme>;
  value?: string;
  multiline?: boolean;
  maxRows?: number;
  debounceMs?: number;
};

function TextInputComponent({
  ariaLabel,
  disabled = false,
  autoFocus = false,
  errorType = 'normal',
  placeholder,
  prefix,
  suffix,
  inputProps,
  sx,
  value,
  onBlur,
  onChange,
  onFocus,
  error,
  helperText,
  multiline = false,
  maxRows,
  enterBlur = true,
  forbiddenCharacters,
  debounceMs = 500,
}: Props) {
  const { t } = useTranslation();
  const [showInvalidCharMsg, setShowInvalidCharMsg] = useState(false);
  const ref = useRef<HTMLInputElement>(null);
  const allForbiddenCharacters = `${ALWAYS_FORBIDDEN_CHARACTERS}${forbiddenCharacters ?? ''}`;
  const { onChangeInner, innerValue, onBlurInner, innerError } = useChangeEvent(
    value ?? '',
    onBlur ?? (() => undefined),
    onChange ?? (() => undefined),
    ref,
    multiline,
    debounceMs,
  );

  const { onKeyUp } = useEnterBlur(ref);

  const forbiddenCharRegex = useMemo(() => {
    if (!allForbiddenCharacters) return undefined;

    const escapedCharString = allForbiddenCharacters.replace(escapeSpecialRegexCharsRegex, '\\$&');

    return new RegExp(`[${escapedCharString}]`, 'g');
  }, [allForbiddenCharacters]);

  const handleFocus = useCallback(
    (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const inputElement = event.currentTarget;

      window.requestAnimationFrame(() => {
        if (document.activeElement === inputElement) {
          inputElement.select();
        }
      });

      onFocus?.(event);
    },
    [onFocus],
  );

  const handleBlur = useCallback(() => {
    onBlurInner();
    setShowInvalidCharMsg(false);
  }, [onBlurInner]);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const nextValue = event.target.value;
      let sanitizedValue = nextValue;

      if (forbiddenCharRegex) {
        sanitizedValue = nextValue.replace(forbiddenCharRegex, '');
      }

      const containsForbiddenChar = sanitizedValue !== nextValue;
      setShowInvalidCharMsg(containsForbiddenChar);
      onChangeInner(sanitizedValue);
    },
    [forbiddenCharRegex, onChangeInner],
  );

  const invalidCharactersTooltipTitle = allForbiddenCharacters
    ? t('common:invalid_characters', {
        charactersStr: allForbiddenCharacters,
      })
    : '';

  const shouldShowInvalidCharMsg = Boolean(allForbiddenCharacters) && showInvalidCharMsg;

  return (
    <StyledTooltip
      placement='bottom'
      title={invalidCharactersTooltipTitle}
      forceState={shouldShowInvalidCharMsg ? 'open' : 'close'}
    >
      <TextField
        inputRef={ref}
        disabled={disabled}
        error={!!innerError || error}
        helperText={helperText || innerError}
        onBlur={handleBlur}
        onChange={handleChange}
        onFocus={handleFocus}
        placeholder={placeholder}
        onKeyUp={enterBlur ? onKeyUp : undefined}
        sx={sx}
        value={innerValue}
        multiline={multiline}
        maxRows={maxRows}
        variant='filled'
        InputProps={{
          ...inputProps,
          inputProps: {
            'aria-label': ariaLabel,
          },
          disableUnderline: true,
          startAdornment: prefix && (
            <InputAdornment sx={{ marginBottom: 2 }} position='start'>
              {prefix}
            </InputAdornment>
          ),
          endAdornment: suffix && <InputAdornment position='end'>{suffix}</InputAdornment>,
        }}
        FormHelperTextProps={{
          className: errorType === 'floating' ? 'Mui-error-floating' : '',
        }}
      />
    </StyledTooltip>
  );
}

export const TextInput = memo(TextInputComponent);
